import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import authService from '../../services/authService';
import chatService from '../../services/chatService';
import type { User } from '../../contracts/authContracts';

export type { User } from '../../contracts/authContracts';

export interface AuthState {
  user: User | null;
  loading: boolean;
  timezone: string | null;
}

const initialState: AuthState = {
  user: null,
  loading: true,
  timezone: null,
};

export const hydrateAuth = createAsyncThunk('auth/hydrate', async (_, { dispatch }) => {
  const currentUser = authService.getCurrentUser();
  if (currentUser) {
    const user: User = {
      token: currentUser.token,
      comercioKey: currentUser.comercioKey,
      email: currentUser.email,
      timezone_iana: currentUser.timezone_iana,
    };
    dispatch(setUser(user));
    dispatch(setTimezone(currentUser.timezone_iana || 'UTC'));
  }
  return true;
});

export const loginThunk = createAsyncThunk('auth/login', async (args: { email: string; password: string }, { dispatch, rejectWithValue }) => {
  try {
    const data = await authService.login(args.email, args.password);
    const currentUser = authService.getCurrentUser();
    if (currentUser && !data.requires_2fa) {
      const user: User = {
        token: currentUser.token,
        comercioKey: currentUser.comercioKey,
        email: currentUser.email,
        timezone_iana: currentUser.timezone_iana,
      };
      dispatch(setUser(user));
      dispatch(setTimezone(currentUser.timezone_iana || 'UTC'));
    }
    return data;
  } catch (e: any) {
    const status = e?.response?.status ?? 500;
    const data = e?.response?.data ?? { message: e?.message ?? 'Request failed' };
    return rejectWithValue({ status, data });
  }
});

export const loginWithGoogleThunk = createAsyncThunk(
  'auth/loginWithGoogle',
  async (
    args: { credential: string; comercio_key?: string; timezone_iana?: string },
    { dispatch, rejectWithValue }
  ) => {
    try {
      const data = await authService.loginWithGoogle(args);
      const currentUser = authService.getCurrentUser();
      if (currentUser) {
        const user: User = {
          token: currentUser.token,
          comercioKey: currentUser.comercioKey,
          email: currentUser.email,
          timezone_iana: currentUser.timezone_iana,
        };
        dispatch(setUser(user));
        dispatch(setTimezone(currentUser.timezone_iana || 'UTC'));
      }
      return data;
    } catch (e: any) {
      // Normalize axios error into a serializable payload
      const status = e?.response?.status ?? 500;
      const data = e?.response?.data ?? { message: e?.message ?? 'Request failed' };
      return rejectWithValue({ status, data });
    }
  }
);

export const logoutThunk = createAsyncThunk('auth/logout', async (_, { dispatch }) => {
  authService.logout();
  try {
    chatService.destroy();
  } catch { /* Best-effort cleanup. */ }
  // Reset Google Identity Services state to avoid a "dead" first attempt after logout
  try {
    // @ts-ignore
    (window as any)?.google?.accounts?.id?.cancel?.();
    // @ts-ignore
    (window as any)?.google?.accounts?.id?.disableAutoSelect?.();
  } catch { /* Best-effort cleanup. */ }
  dispatch(setUser(null));
  dispatch(setTimezone(null));
  return true;
});

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setUser(state, action: PayloadAction<User | null>) {
      state.user = action.payload;
      state.loading = false;
    },
    setTimezone(state, action: PayloadAction<string | null>) {
      state.timezone = action.payload || null;
    },
    setLoading(state, action: PayloadAction<boolean>) {
      state.loading = !!action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(hydrateAuth.pending, (state) => {
        state.loading = true;
      })
      .addCase(hydrateAuth.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(hydrateAuth.rejected, (state) => {
        state.loading = false;
      });

    builder
      .addCase(loginThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(loginThunk.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(loginThunk.rejected, (state) => {
        state.loading = false;
      });

    builder
      .addCase(loginWithGoogleThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(loginWithGoogleThunk.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(loginWithGoogleThunk.rejected, (state) => {
        state.loading = false;
      });

    builder.addCase(logoutThunk.fulfilled, (state) => {
      state.user = null;
      state.timezone = null;
      state.loading = false;
    });
  },
});

export const { setUser, setTimezone, setLoading } = authSlice.actions;

export default authSlice.reducer;
